/**
 * 分享對話框組件
 */

import React, { useState, useEffect } from 'react';
import { Modal, Select, Input, message, Spin } from 'antd';
import { ShareAltOutlined } from '@ant-design/icons';
import { getCompanyUsers, shareRecord, CompanyUser } from '../services/reviewHistoryService';

const { TextArea } = Input;

interface ShareModalProps {
  visible: boolean;
  onClose: () => void;
  recordType: 'review' | 'comparison';
  recordId: number;
  recordTitle: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  visible,
  onClose,
  recordType,
  recordId,
  recordTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [shareMessage, setShareMessage] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // 載入公司用戶列表
  useEffect(() => {
    if (visible) {
      loadCompanyUsers();
    }
  }, [visible]);

  const loadCompanyUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getCompanyUsers();
      setUsers(response.users);
    } catch (error: any) {
      console.error('載入用戶列表失敗:', error);
      message.error('載入用戶列表失敗: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) {
      message.warning('請至少選擇一位同事');
      return;
    }

    setLoading(true);
    try {
      await shareRecord({
        record_type: recordType,
        record_id: recordId,
        shared_to: selectedUsers,
        message: shareMessage.trim() ? shareMessage : undefined,
      });

      message.success(`已成功分享給 ${selectedUsers.length} 位同事`);
      handleClose();
    } catch (error: any) {
      console.error('分享失敗:', error);
      message.error('分享失敗: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setShareMessage('');
    onClose();
  };

  return (
    <Modal
      title={
        <span>
          <ShareAltOutlined style={{ marginRight: 8, color: '#1E88A8' }} />
          分享記錄
        </span>
      }
      open={visible}
      onOk={handleShare}
      onCancel={handleClose}
      okText='分享'
      cancelText='取消'
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, color: '#666' }}>
          <strong>分享內容：</strong>
        </div>
        <div
          style={{
            padding: 12,
            background: '#f5f5f5',
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          {recordTitle}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, color: '#666' }}>
          <strong>選擇分享對象：</strong>
          <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>（公司內部同事）</span>
        </div>
        {loadingUsers ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin tip='載入用戶列表中...' />
          </div>
        ) : (
          <Select
            mode='multiple'
            placeholder='請選擇要分享的同事'
            style={{ width: '100%' }}
            value={selectedUsers}
            onChange={setSelectedUsers}
            optionFilterProp='label'
            showSearch
            maxTagCount='responsive'
          >
            {users.map(user => (
              <Select.Option key={user.id} value={user.id} label={`${user.name} (${user.email})`}>
                <div>
                  <div style={{ fontWeight: 500 }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{user.email}</div>
                </div>
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      <div>
        <div style={{ marginBottom: 8, color: '#666' }}>
          <strong>分享訊息：</strong>
          <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>（選填）</span>
        </div>
        <TextArea
          placeholder='輸入分享訊息，例如：「請參考這份審查結果」'
          value={shareMessage}
          onChange={e => setShareMessage(e.target.value)}
          rows={3}
          maxLength={200}
          showCount
        />
      </div>

      <div
        style={{
          marginTop: 16,
          padding: 12,
          background: '#e6f4f8',
          borderRadius: 6,
          fontSize: 12,
          color: '#0958d9',
        }}
      >
        <div style={{ fontWeight: 500, marginBottom: 4 }}>📌 分享說明：</div>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>分享後，接收者可以在自己的歷史記錄中看到此記錄</li>
          <li>接收者可以查看完整的審查結果和內容</li>
          <li>只能分享給同公司的同事</li>
        </ul>
      </div>
    </Modal>
  );
};

export default ShareModal;
